import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Receipt } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import SignInLog from "@/components/SignInLog";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function StudentDashboard() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api.get("/my/courses").then(({ data }) => setCourses(data)).catch(() => {});
    api.get("/my/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, []);
  const downloadInvoice = async (orderId) => {
    try {
      const res = await api.get(`/my/orders/${orderId}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `Invoice-${orderId}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { /* toast handled by axios interceptor if any */ }
  };
  if (!user) return <PublicLayout><div className="container-x py-24">Please <Link to="/login" className="text-gold">log in</Link>.</div></PublicLayout>;
  return (
    <PublicLayout>
      <section className="container-x pt-16 pb-24">
        <div className="overline mb-4">Welcome back, {user.name}</div>
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter text-ink-900">My courses</h1>
        <div className="mt-14 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {courses.length === 0 ? (
              <div className="glass rounded-2xl p-10 text-center">
                <p className="text-ink-600">You haven't enrolled in a course yet.</p>
                <Link to="/courses" className="btn-neon mt-6 inline-flex" data-testid="dashboard-browse">Browse courses</Link>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6" data-testid="dashboard-courses">
                {courses.map((c) => (
                  <div key={c.course_id} className="glass rounded-2xl overflow-hidden" data-testid={`enrolled-${c.slug}`}>
                    <img src={c.thumbnail} alt="" className="aspect-video object-cover w-full" />
                    <div className="p-6 space-y-3">
                      <div className="font-display text-xl text-ink-900">{c.title}</div>
                      <div className="h-2 bg-ink-900/10 rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${c.progress_percent}%` }} /></div>
                      <div className="text-xs text-ink-500">{c.completed_lessons}/{c.total_lessons} lessons • {c.progress_percent}% complete</div>
                      <Link to={`/learn/${c.slug}`} className="btn-neon w-full text-sm !py-2.5" data-testid={`continue-${c.slug}`}>Continue learning</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {orders.length > 0 && (
              <div className="glass rounded-2xl p-6 mt-10" data-testid="dashboard-invoices">
                <div className="flex items-center gap-2 mb-5">
                  <Receipt className="w-4 h-4 text-gold" />
                  <div className="overline">Invoices &amp; Receipts</div>
                </div>
                <div className="divide-y divide-ink-900/5">
                  {orders.map((o) => (
                    <div key={o.order_id} className="py-4 flex items-center justify-between gap-4" data-testid={`invoice-row-${o.order_id}`}>
                      <div className="min-w-0">
                        <div className="text-ink-900 font-medium truncate">{o.course_title}</div>
                        <div className="text-xs text-ink-500 mt-0.5">
                          <span className="font-mono">{o.order_id}</span>
                          <span className="mx-2">·</span>
                          {(o.paid_at || o.created_at || "").slice(0, 10)}
                          <span className="mx-2">·</span>
                          {money(o.final_price)}
                        </div>
                      </div>
                      <button
                        onClick={() => downloadInvoice(o.order_id)}
                        className="btn-ghost text-xs !py-2 !px-3 inline-flex items-center gap-1.5 flex-shrink-0"
                        data-testid={`download-invoice-${o.order_id}`}
                      >
                        <Download className="w-3.5 h-3.5" /> Invoice
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside className="space-y-6">
            <SignInLog />
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}
